(ns arthur.domain.channel
  "A channel is one animatable property, sampled at a frame.

  Three shapes, and the uniformity across them is the entire point of the model
  — analysis does not produce a different kind of data, it produces keys densely
  on the same channels a hand fills in sparsely:

    FRAMED  {:animated? false :value v}
            A thing that simply exists. A painted background cel is this.

    KEYED   {:animated? true :interp :hold :keys {0 v, 4 v, 12 v}}
            Sparse, authored, in the document. Undoable and syncable.

    DENSE   {:animated? true :interp :hold
             :dense {:store \"sha256:…\" :offset 0 :stride 40 :frames 600}
             :generated {...}}
            Generated, one value per frame, in a typed array outside app-db.

  `value-at` reads all three and is the specification. `cursor`/`sample!` is the
  fast path for playback and must agree with it exactly; scene-test asserts that
  across forward, backward and random frame order, because a cursor that drifts
  is a bug you would see as the wrong pose rather than as an error.

  KEYS ARE A MAP BY FRAME, NEVER A VECTOR, and the map stored in the document is
  a PLAIN map — transit and JSON both lose sortedness, so the sorted index is
  built here at read time and never persisted.

  :generated is provenance. NOTHING IN HERE READS IT, and nothing downstream may:
  it exists so the UI can offer a parameter panel instead of raw keys. It lives
  on the channel rather than the node because a mouth wants a rotoscoped
  [:geom :pts] and a hand-animated [:xform :pos] at the same time, and putting
  the flag on the node would forbid the most useful thing in the model.")

;; ---------------------------------------------------------------------------
;; the state mask
;;
;; PRESENCE IS NOT VISIBILITY, and the distinction is free now and expensive to
;; retrofit. A part that is hidden EXISTS and is not drawn, which is `[:vis]`, a
;; channel like any other. A subject that is occluded has NO VALUE on that frame
;; — there is nothing to hide and nothing to fall back on — and that is this.
;;
;; The mask carries ABSENCE ONLY. An earlier draft gave it a hidden bit as well,
;; per docs/architecture.md's "hidden flag + palette index", and that bit was
;; simply a dense `[:vis]` wearing a different hat: two mechanisms for one
;; question, which is how you end up with a part that is hidden by one and shown
;; by the other. Hiding is a channel; absence is a state. Remaining bits are
;; reserved.

(def ^:const present 0)
(def ^:const absent-bit 1)

(def absent
  "Sampled value for a frame the subject was not on.

  Distinct from a part being switched off, which is `[:vis]` being false, and
  distinct from a part having no keys. The identity tracker, when it arrives,
  needs somewhere to say \"not on screen\" without inventing a pose."
  ::absent)

(defn nothing?
  "True when there is no value to draw with."
  [v]
  (identical? v absent))

;; ---------------------------------------------------------------------------
;; constructors, for hand-written scenes and tests

(defn framed [v] {:animated? false :value v})

(defn keyed
  "A channel of keys, and how each one leads to the next. `interp` is an
  argument, never a default: `:hold` and `:linear` are the difference between a
  cut and a tween, which is the whole content of the channel."
  [ks interp]
  {:animated? true :interp interp :keys ks :over []})

;; ---------------------------------------------------------------------------

(defn component
  "Component i of a multi-component channel value.

  An authored value is a CLJS vector; a value read out of a dense block is a
  typed-array view over the block, because copying it would allocate per node
  per frame. Both have to read the same way here or every consumer downstream
  grows the same two-way branch."
  [v i]
  (if (vector? v) (-nth v i) (aget v i)))

(defn frames
  "Sorted vector of the frames a keyed channel has keys on, or nil. Built here
  and cached by `cursor`; `value-at` rebuilds it, which is why `value-at` is the
  specification and not the playback path."
  [ch]
  (when-let [ks (:keys ch)]
    (vec (sort (keys ks)))))

;; ---------------------------------------------------------------------------
;; correction layers
;;
;; `:over` is an ORDERED STACK on top of whatever the channel already says.
;; Generated motion stays the base; a hand correction is a layer above it, so
;; regenerating replaces the base and the corrections survive. That is the whole
;; reason the stack exists rather than the hand edit being written into the keys.
;;
;; A LAYER'S VALUES ARE A CHANNEL. A constant adjustment is a framed one, a ramp
;; or a return motion is a keyed one, and neither needs a second way of saying
;; what a value is over time: layers read through `value-at` and `cursor` like
;; anything else, which is also what stops the fast path and the specification
;; from being two implementations of blending.
;;
;; A LAYER HAS NO TIME SPACE OF ITS OWN. Its `:support` and its values' keys are
;; in the frames the base channel's keys are in — the node's own. A correction on
;; a lane is therefore in lane frames and crosses the drawing boundaries under
;; it; a correction on one cel is in that cel's frames and travels
;; with it when it moves. Ownership already answered the question, so there is no
;; field to get wrong.

(defn layer
  "One correction: `values` applied to the base wherever `support` covers the
  frame. `op` is `:offset` or `:replace`."
  [id support op values]
  {:id id :support support :op op :values values})

(defn- covers?
  "Half-open, as a span is: a correction over frames 10 to 12 is `[10 13)`."
  [[in out] f]
  (and (<= in f) (< f out)))

(defn- width
  "Components in a value, or nil for a number. A dense value is a typed-array
  view, an authored one a vector, and a correction has to add to either."
  [v]
  (cond (number? v) nil (vector? v) (count v) :else (.-length v)))

(defn- shape
  "What kind of value this is, for asking whether one can be added to another:
  `:scalar`, a component count, or `:opaque` for a value that is neither — a
  `[:vis]` boolean is opaque, and can be replaced but not offset."
  [v]
  (cond
    (number? v) :scalar
    (vector? v) (count v)
    (and (some? v) (number? (.-length v))) (.-length v)
    :else :opaque))

(defn value-shape
  "The shape of the values a channel yields, without sampling it, or nil where
  there is nothing to read it off — an empty key map says nothing about what its
  values would have been, and nil must not be taken for a scalar."
  [ch]
  (cond
    (not (:animated? ch)) (when (some? (:value ch)) (shape (:value ch)))
    (:dense ch)           (if (= 1 (:stride (:dense ch))) :scalar (:stride (:dense ch)))
    (seq (:keys ch))      (shape (val (first (:keys ch))))
    :else                 nil))

(defn- shape-conflict [base-shape correction-shape]
  (cond
    (or (nil? base-shape) (nil? correction-shape)) nil
    (= :opaque base-shape) "the base is not a number or a row of components"
    (= :opaque correction-shape) "the correction is not a number or a row of components"
    (not= base-shape correction-shape)
    (str "the base has " (pr-str base-shape) " and the correction "
         (pr-str correction-shape) " — a correction cannot offset a value of"
         " a different shape")))

(defn conflict-with
  "Why correction `l` cannot apply to base channel `base`, or nil.

  ONLY `:offset` can conflict. It adds component by component, so it needs the
  base to have the components it has — which is what a topology change takes
  away when a re-freeze gives a mouth a different number of points. `:replace`
  states a whole value and so has nothing to agree with.

  Shapes that cannot be read yet do not conflict: an empty key map is not a
  disagreement, it is a channel with nothing in it."
  [base l]
  (when (= :offset (:op l))
    (shape-conflict (value-shape base) (value-shape (:values l)))))

(defn- support-of [l]
  (let [s (:support l)]
    (when (and (vector? s) (= 2 (count s))
               (every? number? s) (< (first s) (second s)))
      s)))

(defn stack-conflict
  "Why layer `i` can encounter a value of the wrong shape after the active
  layers before it, or nil.

  Replacement coverage is considered at every interval boundary. This matters
  when adjacent replacements jointly cover an offset: neither covers its whole
  support, but the base can never reach it. A conflicted replacement is skipped,
  exactly as the evaluator skips it."
  [ch i]
  (let [l (nth (:over ch) i nil)]
    (when (and (= :offset (:op l)) (support-of l))
      (let [[a b] (support-of l)
            prior (take i (:over ch))
            cuts (->> prior
                      (keep support-of)
                      (mapcat identity)
                      (filter #(< a % b))
                      (into [a b])
                      distinct sort)
            ;; Shape at a point is the last active, nonempty replacement's
            ;; shape, or the base shape when no replacement supplies a value.
            at (fn [f]
                 (or (last (keep (fn [p]
                                   (let [s (support-of p)
                                         v (value-shape (:values p))]
                                     (when (and (= :replace (:op p))
                                                (not (:conflict p)) v s
                                                (covers? s f))
                                       v)))
                                 prior))
                     (value-shape ch)))
            shapes (into #{} (map (fn [[x y]] (at (/ (+ x y) 2))))
                         (partition 2 1 cuts))
            v (value-shape (:values l))]
        (some #(shape-conflict % v) shapes)))))

(defn reconcile
  "Recheck an ordered layer stack against this channel's base.

  Old conflict marks are findings from an earlier base, so they are cleared and
  recomputed in order. A newly conflicted replacement is then invisible to the
  layers after it, matching evaluation. Nothing is dropped or reordered."
  [ch]
  (let [layers (mapv #(dissoc % :conflict) (:over ch))]
    (reduce (fn [out l]
              (let [candidate (assoc ch :over (conj out l))
                    why (stack-conflict candidate (count out))]
                (conj out (cond-> l why (assoc :conflict why)))))
            [] layers)))

(defn conflicts
  "Corrections on `ch` that cannot apply to its base, as `[{:id :why}]`.

  NOT `problems`. A conflict is a legitimate state for a document to be in: a
  regeneration changed the topology under a correction that was right when it was
  made, and resolving it is a person's decision, not a reason the document will
  not load. `flow/regenerate` records one on the layer, a conflicted layer is not
  applied, and this is how a view finds them to offer."
  [ch]
  (vec (for [[i l] (map-indexed vector (:over ch))
             :let [why (or (:conflict l) (stack-conflict ch i))]
             :when why]
         {:id (:id l) :why why})))

(defn- offset-onto
  "`base` plus `v`, component-wise. A vector, never a write into `base`, which
  for a dense channel is a view onto the block itself."
  [base v ch]
  (let [wb (width base) wv (width v)]
    (cond
      (and (nil? wb) (nil? wv)) (+ base v)
      (and wb wv (= wb wv))
      (mapv (fn [i] (+ (component base i) (component v i))) (range wb))
      :else
      (throw (ex-info "a correction cannot offset a value of a different shape"
                      {:base wb :correction wv :channel (dissoc ch :dense)})))))

(defn- eye-opening-onto [points amount]
  (let [n (width points)
        ys (map #(component points %) (range 1 n 2))
        center (/ (+ (reduce min ys) (reduce max ys)) 2)]
    (mapv (fn [i] (let [v (component points i)]
                    (if (odd? i) (+ center (* amount (- v center))) v)))
          (range n))))

(defn- over-at
  "Fold `ch`'s layers onto `base` at frame f. `read` samples one layer's values
  and is the only thing that differs between the specification and the cursor."
  [ch f base read]
  (reduce-kv
   (fn [v i {:keys [support op values conflict]}]
     ;; A conflicted correction is neither applied nor forgotten: it stays in
     ;; the document, `conflicts` reports it, and a person decides. Applying it
     ;; would misapply it; removing it would throw away hand work.
     (if (or conflict (not (covers? support f)))
       v
       (let [x (read i values f)]
         (cond
           (nothing? x)    v
           (= :replace op) x
           ;; `replace` can supply a value over an absent base; `offset` has
           ;; nothing to add to and says so rather than inventing a pose.
           (nothing? v)    absent
           (= :eye-opening op) (eye-opening-onto v x)
           :else           (offset-onto v x ch)))))
   base
   (vec (:over ch))))

;; ---------------------------------------------------------------------------
;; dense blocks

(defn- absent-at?
  "Is the subject absent on frame f of this block's slice?

  INDEXED THE WAY THE DATA IS. A block is node-major — offset(node i) =
  i·frames·stride — so a block holding several nodes holds several mask regions,
  and the one belonging to this channel starts at offset/stride. Indexing the
  mask by f alone reads the FIRST node's absence for every node in the block,
  which is not a subtly wrong pose: it is every part in the block vanishing on
  the frames where one of them was occluded."
  [state offset stride f]
  (and (some? state)
       (pos? (bit-and (aget state (+ (quot offset stride) f)) absent-bit))))

(defn dense-at
  "Read frame f out of a dense block.

  `store` is {store-key -> {:data <typed array> :state <Uint8Array or nil>}},
  tier 2, behind a handle and never in app-db.

  The frame is CLAMPED into the block. A time map with an offset deliberately
  reads the future — mouth lead is the whole reason `:offset` exists — so the
  last frame of a leading track is asked for a frame past the end on every one
  of the last `lead` frames. Clamping there is what the JS `shiftIndex` does and
  it is the right answer: the track holds its final pose. Returning nothing
  instead would blank the mouth at the end of every take.

  stride 1 yields a number; anything wider yields a SUBARRAY VIEW over the
  block, not a copy. Fixed topology is what makes that possible — the frame's
  data is a rectangular slice at a known offset with no per-frame header.

  FIXED POINT. `:scale` in the block header means the stored integers are the
  value times that scale, so a block of geometry in image-height units fills an
  Int16 usefully and a block of stage pixels — which wants a different scale
  entirely — fills one too. It is in the header rather than agreed by convention
  for exactly that reason, and it is why the block in memory is byte for byte the
  block on the wire: a handle that names a sha256 has to name the bytes you
  actually hold.

  Decoding costs the view. `out` is a stride-sized destination the caller owns —
  `cursor` allocates one per channel — because a copy per node per frame is the
  allocation this whole model is arranged to avoid; passing nil allocates, which
  is what `value-at`, the specification, does — and it says so by passing nil,
  because there is no arity here that decides it for a caller."
  [{:keys [store offset stride scale] nf :frames} f st out]
   (let [{:keys [data state]} (get st store)]
     (when (nil? data)
       (throw (ex-info "dense channel's store key is not in the store"
                       {:store store :have (vec (sort (map str (keys st))))})))
     (let [f (-> f (max 0) (min (dec nf)))]
       (if (absent-at? state offset stride f)
         absent
         (let [o (+ offset (* f stride))]
           (cond
             (= 1 stride) (let [v (aget data o)] (if scale (/ v scale) v))
             (nil? scale) (.subarray data o (+ o stride))
             :else        (let [dst (or out (js/Float64Array. stride))]
                            (dotimes [k stride]
                              (aset dst k (/ (aget data (+ o k)) scale)))
                            dst)))))))

;; ---------------------------------------------------------------------------
;; the specification

(defn segment-interp
  "How the key at `left` leads to the next key. A channel default remains useful
  for uniform tracks; :segments overrides only the gaps an artist chose."
  [ch left]
  (get (:segments ch) left (:interp ch)))

(defn- interpolate [ch f left right]
  (let [a (get (:keys ch) left)]
    (if (and (= :linear (segment-interp ch left)) right (>= f left) (> right left))
      (let [b (get (:keys ch) right)
            t (/ (- f left) (- right left))]
        (cond
          ;; Palette-choice channels interpolate identities into a blend
          ;; descriptor. The renderer keeps indexed geometry in the left
          ;; palette's bank and blends that bank's ramp toward the right one.
          ;; Palette ids are opaque identities. Built-ins happen to use
          ;; keywords, while palettes made in the editor use UUIDs; treating
          ;; the latter as numbers produces NaN and therefore the renderer's
          ;; pink bad-data sentinel.
          (and (= :palette (:semantic ch)) (some? a) (some? b))
          {:from a :to b :t t}
          (vector? a)
          (mapv (fn [x y] (+ x (* t (- y x)))) a b)
          :else (+ a (* t (- b a)))))
      a)))

(defn- keyed-at
  "The most recent key at or before f, CLAMPED to the first key below it.

  Hold is the default and clamping at the low end is the JS `activeKey`'s
  behaviour, kept: a channel's first key is the pose the part starts in, so a
  frame before it reads that pose rather than having no value. This is not the
  same question as presence — a part with no value at all is `absent`, which is
  a state bit, not an empty key map."
  [ch f]
  (let [fr (sort (keys (:keys ch)))
        left (or (last (take-while #(<= % f) fr)) (first fr))
        right (first (drop-while #(<= % f) fr))]
    (interpolate ch f left right)))

(defn repair-frame
  "Map a damaged frame to a donor in the current base. Latest interval wins;
  donors are sampled directly, never recursively through other repairs."
  [ch f]
  (reduce (fn [frame {:keys [from through donor]}]
            (if (<= from f through) donor frame))
          f (:repairs ch)))

(defn value-at
  "Sample a channel at frame f. THE SPECIFICATION — correct, allocating, and
  O(n) in the keys. `cursor`/`sample!` is what playback uses.

  `store` IS AN ARGUMENT, NEVER A DEFAULT. A dense channel cannot be read
  without the tier-2 store it names, and an arity that filled in nil let a
  caller omit it, read correctly for every channel that happened not to be
  dense, and throw the first time a selection landed on one that was. That is
  how `gesture/values` took the stage down on an iris. A caller with no store
  says `nil` and means it."
  ([ch f store] (value-at ch f f store))
  ([ch base-f correction-f store]
   (let [base-f (repair-frame ch base-f)
         base (cond
                (not (:animated? ch)) (:value ch)
                (:dense ch)           (dense-at (:dense ch) base-f store nil)
                (:keys ch)            (let [ks (:keys ch)]
                                        (if (empty? ks) absent (keyed-at ch base-f)))
                :else
                (throw (ex-info "animated channel has neither :keys nor :dense"
                                {:channel ch})))]
     (if (seq (:over ch))
       (over-at ch correction-f base
                (fn [_ values f] (value-at values f store)))
       base))))

;; ---------------------------------------------------------------------------
;; the playback path
;;
;; Playback is SEQUENTIAL, so "the most recent key at or before f" is an advance
;; of a saved index rather than a search. The difference at 30fps is a `sort` and
;; a `take-while` allocation per channel per frame against none, which is the
;; difference between the model being usable and being a demo.

(defn- bsearch
  "Largest index i with ks[i] <= f, or 0 when f precedes every key (hold clamps
  low, see keyed-at)."
  [ks f]
  (loop [lo 0, hi (dec (count ks)), best 0]
    (if (> lo hi)
      best
      (let [mid (bit-shift-right (+ lo hi) 1)]
        (if (<= (nth ks mid) f)
          (recur (inc mid) hi mid)
          (recur lo (dec mid) best))))))

(deftype Cursor [ch ks store buf overs ^:mutable i]
  Object
  (toString [_] (str "#Cursor{" (pr-str (if ks :keyed (if (:dense ch) :dense :framed))) " i=" i "}")))

(defn cursor
  "A reading head on one channel. Build once per channel per resolver, then
  `sample!` it per frame. Holds the sorted key index, which is why the index is
  built here and not in the document — and the decode buffer a fixed-point block
  needs, for the same reason the resolver owns one point buffer per node.

  Only a wide fixed-point block gets a buffer: a stride-1 block decodes to a
  number and a block with no `:scale` is handed back as a view.

  A correction layer gets a reading head of its own, because its values are a
  channel and this is how a channel is read fast. One level deep: a layer's
  values may not themselves carry layers, which `problems` refuses.

  `store` is an argument for the reason it is one on `value-at`."
  [ch store]
  (let [d (:dense ch)]
    (->Cursor ch
              (when (and (:animated? ch) (not d) (seq (:keys ch))) (frames ch))
              store
              (when (and d (:scale d) (> (:stride d) 1))
                (js/Float64Array. (:stride d)))
              (mapv #(cursor (:values %) store) (:over ch))
              0)))

(defn- base-sample!
  "What the cursor's channel says at f BEFORE its corrections. Advancing the
  reading head is this function's whole job, and it is separate from blending so
  that a layer cannot accidentally be read through the base's index."
  [^Cursor cur ch ks f]
  (cond
    (not (:animated? ch)) (:value ch)
    (:dense ch)           (dense-at (:dense ch) f (.-store cur) (.-buf cur))
    (nil? ks)             absent          ; animated with an empty key map
    :else
    (let [n    (count ks)
          i    (.-i cur)
          last (dec n)
          i'   (cond
                 ;; still inside the key the cursor sits on
                 (and (<= (nth ks i) f)
                      (or (= i last) (> (nth ks (inc i)) f)))
                 i
                 ;; the next one — one frame of playback crossed one key
                 (and (< i last)
                      (<= (nth ks (inc i)) f)
                      (or (= (inc i) last) (> (nth ks (+ i 2)) f)))
                 (inc i)

                 :else (bsearch ks f))]
      (set! (.-i cur) i')
      (interpolate ch f (nth ks i') (when (< i' last) (nth ks (inc i')))))))

(defn sample!
  "Value of the cursor's channel at f. O(1) when f is at or one key past where
  the cursor already sits — the playback case — and O(log n) otherwise, which is
  a seek. Advancing and seeking are deliberately different costs: a scrub can
  afford a binary search and a frame cannot.

  A correction layer is sampled through its OWN cursor, so a stacked channel is
  still one reading head per key map and `value-at` stays the specification for
  the blending as well as for the base."
  ([cur f] (sample! cur f f))
  ([^Cursor cur base-f correction-f]
   (let [ch   (.-ch cur)
         base (base-sample! cur ch (.-ks cur) (repair-frame ch base-f))]
     (if (seq (:over ch))
       (over-at ch correction-f base
                (fn [i _ f] (sample! (nth (.-overs cur) i) f)))
       base))))

;; ---------------------------------------------------------------------------

(defn describe
  "Which of the three shapes, for error messages and the parameter panel."
  [ch]
  (cond
    (not (:animated? ch)) :framed
    (:dense ch)           :dense
    :else                 :keyed))

(defn problems
  "Human-readable reasons this map is not a channel. Empty means it is one."
  [ch]
  (let [values (when (map? (:keys ch)) (vals (:keys ch)))
        first-value (first values)
        linear-values? (or (every? number? values)
                           (and (= :palette (:semantic ch)) (every? some? values))
                           (and (vector? first-value)
                                (pos? (count first-value))
                                (every? (fn [v] (and (vector? v)
                                                     (= (count v) (count first-value))
                                                     (every? number? v)))
                                        values)))
        linear? (or (= :linear (:interp ch))
                    (some #{:linear} (vals (:segments ch))))]
   (cond-> []
    (and (contains? ch :repairs)
         (not (and (vector? (:repairs ch))
                   (every? (fn [{:keys [id from through donor]}]
                             (and id (every? integer? [from through donor])
                                  (<= 0 from through) (<= 0 donor)))
                           (:repairs ch)))))
    (conj "repairs require an ID and nonnegative whole donor and interval frames")

    (not (map? ch))
    (conj "not a map")

    (and (map? ch) (not (contains? ch :animated?)))
    (conj ":animated? is required — the flag is what makes framed and keyed one type")

    (and (map? ch) (:animated? ch) (not (or (:keys ch) (:dense ch))))
    (conj "animated but has neither :keys nor :dense")

    (and (map? ch) (:animated? ch) (:keys ch) (:dense ch))
    (conj "has both :keys and :dense; a channel is one shape at a time")

    (and (map? ch) (:keys ch) (not (map? (:keys ch))))
    (conj (str ":keys is a " (if (vector? (:keys ch)) "vector" "non-map")
               " — keys are a MAP by frame, so a merge can be per-key"))

    (and (map? ch) (:keys ch) (map? (:keys ch)) (not (every? number? (keys (:keys ch)))))
    (conj ":keys has a non-numeric frame")

    (and (map? ch) (:animated? ch) (not (#{:hold :linear nil} (:interp ch))))
    (conj (str ":interp " (:interp ch) " is not implemented"))

    (and (map? ch) (contains? ch :segments)
         (or (not (map? (:segments ch)))
             (not (:keys ch))
             (not (every? (set (keys (:keys ch))) (keys (:segments ch))))
             (not (every? #{:hold :linear} (vals (:segments ch))))))
    (conj ":segments must map existing key frames to :hold or :linear")

    (and (map? ch) linear?
         (or (:dense ch) (not linear-values?)))
    (conj ":linear interpolation needs numeric keys of one shape")

    (and (map? ch) (contains? ch :over) (not (vector? (:over ch))))
    (conj ":over is an ORDERED stack, so it is a vector")

    (and (map? ch) (vector? (:over ch)))
    (into (for [{:keys [id support op values]} (:over ch)
                p (cond-> []
                    (nil? id)
                    (conj "needs an :id — a correction has an identity a regeneration can keep")

                    (not (and (vector? support) (= 2 (count support))
                              (every? #(and (number? %) (js/Number.isFinite %)) support)
                              (< (first support) (second support))))
                    (conj (str ":support " (pr-str support)
                               " must be a finite, increasing [in out)"))

                    (not (#{:offset :replace :eye-opening} op))
                    (conj (str ":op " (pr-str op) " is not :offset, :replace or :eye-opening"))

                    ;; One level. A layer over a layer is an ordering mechanism
                    ;; the stack already is, and it would make the read
                    ;; unbounded in depth for nothing.
                    (seq (:over values))
                    (conj "a layer's values cannot carry layers of their own")

                    (seq (problems (dissoc values :over)))
                    (conj (str "values are not a channel: "
                               (first (problems (dissoc values :over))))))]
            (str "correction " (pr-str id) " " p)))

    ;; A shape mismatch NOBODY HAS RECORDED is an authoring bug; one a
    ;; regeneration recorded is a conflict awaiting a person, and `conflicts`
    ;; reports those. The distinction is what keeps a topology change from
    ;; making a document that will not load.
    (and (map? ch) (vector? (:over ch)))
    (into (for [[i l] (map-indexed vector (:over ch))
                :when (not (:conflict l))
                :let [why (stack-conflict ch i)]
                :when why]
            (str "correction " (pr-str (:id l)) " " why)))

    ;; A scale of zero divides every value in the block by zero, and a negative
    ;; one mirrors the geometry. Both are authored-data bugs that present as a
    ;; part drawn nowhere or inside out, not as an error.
    (and (map? ch) (:dense ch) (contains? (:dense ch) :scale)
         (not (and (number? (:scale (:dense ch))) (pos? (:scale (:dense ch))))))
    (conj (str ":dense :scale is " (pr-str (:scale (:dense ch)))
               " — a fixed-point scale is a positive number the stored integers"
               " were multiplied by")))))
